import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";

// Une quête qui avance montre ce qu'elle vient de gagner, à la manière des quêtes
// de Duolingo : une étiquette « +3 min » apparaît, la part gagnée se dessine en
// clair sur la barre, puis le remplissage la rejoint pendant que le compteur
// défile chiffre par chiffre. Un reflet traverse la barre à l'arrivée.
const EASE = [0.22, 1, 0.36, 1];
// Le temps de lire l'étiquette avant que la barre ne parte.
const LEAD_IN = 380;

// `gain` : { amount, key, quiet }, posé par QuestList quand la quête vient d'avancer.
// Pas de MotionValue rendue en enfant (Next 16) : la valeur affichée vit dans un
// state mis à jour par requestAnimationFrame.
export function useQuestGain(currentProgress, gain) {
    const reduceMotion = useReducedMotion();
    const target = Number(currentProgress) || 0;
    // Un gain déjà présent au montage n'est pas rejoué.
    const [seenKey, setSeenKey] = useState(gain?.key ?? null);
    const [run, setRun] = useState(null);
    const [value, setValue] = useState(null);
    const [sheen, setSheen] = useState(0);

    if (gain && gain.amount > 0 && gain.key !== seenKey) {
        setSeenKey(gain.key);
        if (!reduceMotion) {
            // Un gain qui arrive pendant le précédent repart de ce qui est affiché.
            const from = value ?? Math.max(0, target - gain.amount);
            setRun({
                from,
                to: target,
                amount: run ? run.amount + gain.amount : gain.amount,
                key: gain.key,
                // Minute regardée pendant une vidéo : la barre avance sans étiquette.
                quiet: Boolean(gain.quiet) && (!run || run.quiet),
            });
            setValue(from);
        }
    }

    useEffect(() => {
        if (!run) return undefined;
        const duration = Math.min(600 + (run.to - run.from) * 90, 1300);
        const startedAt = performance.now();
        let frame;
        const tick = (now) => {
            const t = Math.min(Math.max((now - startedAt - (run.quiet ? 0 : LEAD_IN)) / duration, 0), 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(run.from + (run.to - run.from) * eased);
            if (t < 1) {
                frame = requestAnimationFrame(tick);
            } else {
                setRun(null);
                setValue(null);
                setSheen((count) => count + 1);
            }
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [run]);

    const display = value ?? target;
    return {
        display,
        count: run ? Math.round(display) : target,
        run,
        sheen,
        inFlight: Boolean(run),
    };
}

export function gainUnit(type) {
    if (type === "time_spent") return " min";
    if (type === "xp_gain") return " XP";
    return "";
}

// L'étiquette « +3 min », au bout de la ligne du titre, le temps de l'animation.
export function GainChip({ run, unit }) {
    return (
        <AnimatePresence>
            {run && !run.quiet && (
                <motion.span
                    key="gain"
                    className="inline-flex shrink-0 items-center rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold leading-4 tabular-nums text-white shadow-sm shadow-primary/30"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6, transition: { duration: 0.3, delay: 0.25, ease: EASE } }}
                    transition={{ duration: 0.3, ease: EASE }}
                >
                    +{run.amount}
                    {unit}
                </motion.span>
            )}
        </AnimatePresence>
    );
}

// La barre de la quête, compteur dedans. `fillClass` : couleur du remplissage.
export function QuestBar({ gainState, target, completed, fillClass, emptyTextClass }) {
    const { display, count, run, sheen } = gainState;
    const percent = (n) => `${Math.min(Math.max((n / target) * 100, 0), 100)}%`;
    const filled = display / target;
    const fill = completed ? "bg-emerald-400" : fillClass;

    return (
        <div className="relative h-[18px] overflow-hidden rounded-full bg-slate-200/80 dark:bg-white/10">
            {/* La part gagnée, dessinée en clair avant que le remplissage ne la rejoigne. */}
            <AnimatePresence>
                {run && (
                    <motion.div
                        key="ghost"
                        className={`absolute inset-y-0 left-0 rounded-full ${fillClass} opacity-25`}
                        style={{ width: percent(run.to) }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.25 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: EASE }}
                    />
                )}
            </AnimatePresence>

            <div
                className={`absolute inset-y-0 left-0 overflow-hidden rounded-full transition-colors duration-300 ${fill}`}
                style={{
                    width: percent(display),
                    minWidth: display > 0 ? 18 : 0,
                    // Pendant un gain, la largeur suit l'image par image ; sinon une transition douce.
                    transition: run ? "background-color 300ms" : "width 500ms ease-out, background-color 300ms",
                }}
            >
                {/* Le reflet clair en haut du remplissage. */}
                <span className="absolute inset-x-2 top-[3px] h-[4px] rounded-full bg-white/30" />
                {sheen > 0 && (
                    <motion.span
                        key={sheen}
                        className="absolute inset-y-0 w-10 -skew-x-12 bg-gradient-to-r from-transparent via-white/50 to-transparent"
                        initial={{ left: "-20%" }}
                        animate={{ left: "110%" }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                    />
                )}
            </div>

            {/* Le compteur, deux fois : sombre sur le vide, blanc sur la part remplie
                (découpé à la largeur du remplissage), pour rester lisible à toute avancée. */}
            <span
                className={`absolute inset-0 flex items-center justify-center text-[11px] font-bold tabular-nums ${emptyTextClass}`}
            >
                {count} / {target}
            </span>
            <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center text-[11px] font-bold tabular-nums text-white"
                style={{ clipPath: `inset(0 ${100 - Math.min(Math.max(filled, 0), 1) * 100}% 0 0)` }}
            >
                {count} / {target}
            </span>
        </div>
    );
}

// Une ligne de la quête qui apparaît ou s'efface (récompense et difficulté, pièce
// de puzzle) : le texte s'estompe d'abord, puis la carte se resserre. Pas d'effet
// au montage. L'espace au-dessus de la ligne doit être un padding (pt-2), pour
// partir avec elle.
export function FadeLine({ show, children }) {
    return (
        <AnimatePresence initial={false}>
            {show && (
                <motion.div
                    key="line"
                    className="overflow-hidden"
                    initial={{ height: 0, opacity: 0, y: 4 }}
                    animate={{
                        height: "auto",
                        opacity: 1,
                        y: 0,
                        transition: {
                            height: { duration: 0.35, ease: EASE },
                            opacity: { duration: 0.3, delay: 0.15, ease: EASE },
                            y: { duration: 0.3, delay: 0.15, ease: EASE },
                        },
                    }}
                    exit={{
                        height: 0,
                        opacity: 0,
                        y: -4,
                        transition: {
                            opacity: { duration: 0.25, delay: 0.15, ease: EASE },
                            y: { duration: 0.25, delay: 0.15, ease: EASE },
                            height: { duration: 0.4, delay: 0.35, ease: EASE },
                        },
                    }}
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
